import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

export default function StoreDetailPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const storeData = {
    name: "ONGU Studio 醞玖製研工作室",
    nameEn: "ONGU Studio",
    address: "台北市信義區松仁路 90 號 2 樓",
    phone: "02-2345-6789",
    hours: "週一至週日 10:00–20:00",
    googleMapsUrl: "https://maps.app.goo.gl/ongustudio",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    heroImage: "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=1200",
    images: {
      space: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=800",
      roasting: "https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?w=800",
      brewing: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800",
      atmosphere: "https://images.unsplash.com/photo-1453614512568-c4024d13c247?w=800"
    }
  };

  return (
    <>
      <Helmet>
        <title>信義區咖啡廳推薦｜ONGU Studio 醞玖製研工作室 - 自家烘焙 × 職人手沖</title>
        <meta name="description" content="ONGU Studio 醞玖製研工作室位於信義區，提供自家烘焙精品咖啡、職人手沖服務，結合工業風空間設計與 KITBAY 選物，打造獨特咖啡體驗。" />
        <meta name="keywords" content="信義區咖啡廳, 信義區手沖咖啡, 信義區自家烘焙, ONGU Studio, 醞玖咖啡, 精品咖啡, 職人手沖" />
        <link rel="canonical" href="https://www.ongucoffee.com/zh/store/xinyi-flagship" />
        
        {/* Open Graph */}
        <meta property="og:title" content="信義區咖啡廳推薦｜ONGU Studio 醞玖製研工作室" />
        <meta property="og:description" content="自家烘焙 × 職人手沖 × 工業風空間，信義區最具特色的精品咖啡廳" />
        <meta property="og:image" content={storeData.heroImage} />
        <meta property="og:url" content="https://www.ongucoffee.com/zh/store/xinyi-flagship" />
        <meta property="og:type" content="website" />

        {/* Schema.org LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CafeOrCoffeeShop",
            "name": storeData.name,
            "alternateName": storeData.nameEn,
            "image": storeData.heroImage,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "松仁路 90 號 2 樓",
              "addressLocality": "信義區",
              "addressRegion": "台北市",
              "addressCountry": "TW"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 25.0330,
              "longitude": 121.5654
            },
            "telephone": storeData.phone,
            "openingHours": "Mo-Su 10:00-20:00",
            "priceRange": "$$",
            "servesCuisine": "Coffee",
            "acceptsReservations": "False"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-black">
        {/* Hero Section */}
        <div className="relative h-[60vh] overflow-hidden">
          <img
            src={storeData.heroImage}
            alt="ONGU Studio 信義旗艦店"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
            <div className="max-w-6xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-wide">
                信義區咖啡廳推薦｜ONGU Studio 醞玖製研工作室
              </h1>
              <p className="text-lg text-gray-200 font-light">
                自家烘焙 × 職人手沖 × 工業風空間
              </p>
            </div>
          </div>
        </div>

        {/* Content Container */}
        <div className="max-w-4xl mx-auto px-6 py-16 space-y-16">
          
          {/* Introduction */}
          <section className="space-y-6">
            <p className="text-lg leading-relaxed text-gray-800 dark:text-gray-200 font-light">
              位於台北信義區的 ONGU Studio 醞玖製研工作室，是一間結合自家烘焙、職人手沖與工業風美學的精品咖啡廳。我們相信，每一杯咖啡都是一場感官旅程——從生豆挑選、烘焙曲線到萃取手法，每個環節都經過細膩打磨。
            </p>
            <p className="text-lg leading-relaxed text-gray-800 dark:text-gray-200 font-light">
              不同於一般連鎖咖啡店，ONGU Studio 堅持小批量烘焙，讓每一批豆子都能展現最佳風味。店內採用開放式烘豆區設計，顧客可以親眼見證咖啡豆從生到熟的轉變過程，感受烘焙時的香氣與溫度。
            </p>
            <p className="text-lg leading-relaxed text-gray-800 dark:text-gray-200 font-light">
              空間設計融合鋁銀金屬與黑色極簡元素，搭配 KITBAY 精選器物，營造出沉穩而富有質感的氛圍。無論是獨自享受一杯手沖，或是與朋友分享咖啡故事，這裡都是信義區最適合靜心品味的角落。
            </p>
            <p className="text-lg leading-relaxed text-gray-800 dark:text-gray-200 font-light">
              我們不只是賣咖啡，更希望傳遞一種生活態度——在快節奏的都市中，找到屬於自己的慢時光。歡迎來到 ONGU Studio，讓我們用一杯咖啡，開啟你對精品咖啡的全新認識。
            </p>
          </section>

          {/* Section 1: Space Design */}
          <section className="space-y-6">
            <h2 className="text-3xl font-light text-gray-900 dark:text-white tracking-wide border-b border-gray-300 dark:border-gray-700 pb-4">
              空間設計 × KITBAY 選物美學
            </h2>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  ONGU Studio 的空間由知名設計團隊操刀，以「工業精煉」為核心概念。鋁銀色金屬管線、裸露水泥牆面，搭配暖色調木質吧台，營造出冷冽中帶有溫度的獨特氛圍。
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  店內與 KITBAY 選物品牌合作，陳列精選咖啡器具、手工陶杯與生活道具。每一件物品都經過嚴選，不只是裝飾，更是對生活品味的堅持。
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  挑高天花板與大面落地窗引入自然光線，讓空間在不同時段展現不同表情。無論晨光或夜幕，這裡都是信義區最適合拍照打卡的咖啡秘境。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src={storeData.images.space}
                  alt="ONGU Studio 空間設計"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Section 2: Roasting Process */}
          <section className="space-y-6">
            <h2 className="text-3xl font-light text-gray-900 dark:text-white tracking-wide border-b border-gray-300 dark:border-gray-700 pb-4">
              職人現場烘豆｜透明製程看得見
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                ONGU Studio 最大的特色，就是開放式烘豆區。我們使用專業級烘豆機，每週定期烘焙新鮮批次，確保每一包豆子都在最佳賞味期內送到顧客手中。
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                烘豆師會根據不同產區、品種與處理法，調整烘焙曲線。淺焙保留花果香氣，中焙平衡酸甜，深焙帶出巧克力與堅果韻味。每一杯咖啡，都是烘豆師對風味的精準詮釋。
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                店內不定期舉辦「烘豆體驗課程」，讓咖啡愛好者親手操作烘豆機，了解溫度、時間與風味的微妙關係。這不只是一堂課，更是一場深度的咖啡文化探索。
              </p>
            </div>
            
            {/* YouTube Video */}
            <div className="relative pt-[56.25%] rounded-lg overflow-hidden shadow-lg">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${storeData.youtubeUrl.split('v=')[1]}`}
                title="ONGU Studio 烘豆過程"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </section>

          {/* Section 3: Brewing */}
          <section className="space-y-6">
            <h2 className="text-3xl font-light text-gray-900 dark:text-white tracking-wide border-b border-gray-300 dark:border-gray-700 pb-4">
              淺焙風味 × 季節限定手沖
            </h2>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src={storeData.images.brewing}
                  alt="職人手沖咖啡"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-4">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  ONGU Studio 以淺焙單品為主打，精選衣索比亞、肯亞、哥倫比亞等產區的精品豆。每一款豆子都經過杯測評分，確保風味乾淨、層次豐富。
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  吧台手沖採用 Hario V60、Kalita Wave 等經典濾杯，搭配精準溫控與注水技巧，萃取出最純粹的咖啡風味。每一杯手沖，都是咖啡師對細節的堅持。
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  店內每月推出「季節限定豆單」，從產地故事到風味描述，讓顧客不只喝咖啡，更能了解每一杯咖啡背後的文化與風土。
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Atmosphere */}
          <section className="space-y-6">
            <h2 className="text-3xl font-light text-gray-900 dark:text-white tracking-wide border-b border-gray-300 dark:border-gray-700 pb-4">
              聞香而來｜信義區的咖啡日常
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                走進 ONGU Studio，首先撲鼻而來的是新鮮烘焙的咖啡香氣。這不是香精或人工香料，而是真實的、有溫度的咖啡氣息——那是屬於信義區最獨特的嗅覺記憶。
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                店內氛圍沉穩而不冷漠，適合獨處也適合聚會。無論是晨間開會、午後閱讀，或是夜晚小酌（我們也提供酒精飲品），這裡都能滿足你對咖啡空間的想像。
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                我們不趕客、不限時，只希望每位來訪的客人，都能在這裡找到屬於自己的節奏。ONGU Studio 不只是一間咖啡廳，更是信義區都市人的心靈補給站。
              </p>
            </div>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img
                src={storeData.images.atmosphere}
                alt="ONGU Studio 店內氛圍"
                className="w-full h-auto object-cover"
              />
            </div>
          </section>

          {/* Section 5: Store Info */}
          <section className="bg-gray-50 dark:bg-gray-900 rounded-lg p-8 space-y-6">
            <h2 className="text-3xl font-light text-gray-900 dark:text-white tracking-wide border-b border-gray-300 dark:border-gray-700 pb-4">
              營業資訊
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <svg className="w-6 h-6 text-gray-600 dark:text-gray-400 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">地址</p>
                    <p className="text-gray-600 dark:text-gray-400">{storeData.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <svg className="w-6 h-6 text-gray-600 dark:text-gray-400 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">營業時間</p>
                    <p className="text-gray-600 dark:text-gray-400">{storeData.hours}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <svg className="w-6 h-6 text-gray-600 dark:text-gray-400 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">聯絡電話</p>
                    <p className="text-gray-600 dark:text-gray-400">{storeData.phone}</p>
                  </div>
                </div>

                <div className="pt-4">
                  <a
                    href={storeData.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <span>在 Google 地圖中開啟</span>
                  </a>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="rounded-lg overflow-hidden shadow-lg h-80">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3615.0023456789!2d121.56340!3d25.03300!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDAxJzU4LjgiTiAxMjHCsDMzJzUwLjIiRQ!5e0!3m2!1szh-TW!2stw!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="ONGU Studio 地圖位置"
                />
              </div>
            </div>
          </section>

        </div>
      </div>
    </>
  );
}